/**
 * New Year window decals — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearWindowDecalsPack: ToilePack = {
    slug: 'new-year-window-decals',
    category: 'season',
    name: 'New Year window decals',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'firework', label: 'Firework', subject: 'A firework burst as one single-colour cut design, isolated on a plain ground'},
        {key: 'greeting', label: 'Greeting decal', subject: 'The words "Happy New Year" lettered in an elegant script as a single-colour decal, isolated'},
        {key: 'confetti', label: 'Confetti corner', subject: 'A corner of scattered confetti shapes as one single-colour cut design for a window corner, isolated'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['a clock instead', 'in silver foil', 'as a corner pair for two panes'],
    directionHint: 'try: a clock instead',
    seo: {
        title: 'New Year Window Decal: Firework, Greeting, Confetti',
        metaDescription: 'Nine new year window decal designs — firework burst, lettered greeting, confetti corner — single colour and cut-ready, three styles.',
        h1: 'New year window decal: firework, greeting, confetti',
        tagline: 'Nine decals, three midnight subjects, three styles each.',
        intro: 'Single-colour, cut-ready artwork for the storefront glass: a firework burst, a lettered greeting, a corner of confetti, built with the solid shapes a decal cutter needs. Ask for a clock face, a champagne toast, or your own subject, and the same three styles carry it.',
        useCases: [
            'a storefront or bar window decal',
            'a car window cling for a party',
            'a glass door greeting for guests',
            'a mirror or vanity decal for the season',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine window decal designs — a firework burst, a lettered greeting, a confetti corner — single colour, in three styles.'},
            {q: 'Will this cut cleanly on a vinyl cutter?', a: "The single-colour, solid-shape style is built for cutting; check your specific cutter's file requirements for the final format."},
            {q: 'Is this a static cling or an adhesive decal?', a: 'The pack provides the artwork only, not a finished decal material — how you apply it depends on the vinyl or cling stock you print or cut it on.'},
            {q: 'Can I get a different subject, like a clock face?', a: 'Yes — the direction field takes any new year subject, including a clock face or a champagne toast.'},
            {q: "Can I use this for decals I'm selling?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
